import { useEffect } from "react";

/**
 * Publish the VISUAL viewport as CSS custom properties, so an open keyboard
 * cannot bury the bottom of a sheet.
 *
 * THE PROBLEM. A sheet is `position: fixed; bottom: 0` and sized against the
 * LAYOUT viewport, which the on-screen keyboard does not change on iOS — so the
 * keyboard is drawn over the bottom of the sheet and takes the Save button with
 * it. Android Chrome resizes the layout viewport instead, so `vh` shrinks there
 * and the same code behaves differently on the two platforms. `dvh` does not
 * settle it either: it tracks browser chrome (the collapsing address bar), not
 * the keyboard.
 *
 * `window.visualViewport` is the one thing that reports what is actually
 * visible on both. Two values come out of it:
 *
 *   --zym-vvh  how tall the visible area is       -> the sheet's max-height
 *   --zym-kb   how much is hidden at the bottom   -> the sheet's `bottom`
 *
 * With the keyboard shut, `--zym-kb` is 0 and `--zym-vvh` is the full height,
 * so every rule that reads them behaves exactly as it did before.
 *
 * Refcounted like useIsMobile: several components may want this, and the first
 * to unmount must not strip the properties from under the others.
 */

let holders = 0;

const useVisualViewport = () => {
  useEffect(() => {
    const vv = typeof window !== "undefined" && window.visualViewport;
    if (!vv) return undefined;

    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty("--zym-vvh", `${Math.round(vv.height)}px`);
      /* What the keyboard covers: everything below the visible area's bottom
         edge. offsetTop matters because iOS SCROLLS the visual viewport up
         rather than resizing the layout one, so the visible band can start
         part-way down the page. Clamped at 0 — a negative inset would push the
         sheet off the bottom of the screen. */
      const hidden = window.innerHeight - vv.height - vv.offsetTop;
      root.style.setProperty("--zym-kb", `${Math.max(0, Math.round(hidden))}px`);
    };

    holders += 1;
    publish();
    vv.addEventListener("resize", publish);
    vv.addEventListener("scroll", publish);

    return () => {
      vv.removeEventListener("resize", publish);
      vv.removeEventListener("scroll", publish);
      holders -= 1;
      if (holders <= 0) {
        holders = 0;
        root.style.removeProperty("--zym-vvh");
        root.style.removeProperty("--zym-kb");
      }
    };
  }, []);
};

export default useVisualViewport;
